import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Globe } from "lucide-react";
import { useAdminAuth, AdminLogin, AdminLoading } from "./AdminAuth";
import AdminBar from "./AdminBar";
import {
  invalidateSiteCache,
  invalidateWeeklyCache,
  DEFAULT_WEEKLY,
  DAYS,
} from "./siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-sm border border-[#102C1F]/25 bg-[#F4F1EB] px-4 py-2.5 text-sm text-[#102C1F] outline-none transition-colors duration-300 placeholder:text-[#102C1F]/40 focus:border-[#102C1F]";

const labelCls =
  "mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60";

const SECTIONS = [
  {
    title: "Hero (haut de la page d'accueil)",
    fields: [
      ["hero_line1", "Titre — ligne 1"],
      ["hero_line2", "Titre — ligne 2"],
      ["hero_text", "Texte de présentation", "textarea"],
      ["hero_btn_reserve", "Bouton — Réserver"],
      ["hero_btn_order", "Bouton — Commander"],
      ["hero_btn_dishes", "Bouton — Voir les photos"],
    ],
  },
  {
    title: "Localisation",
    fields: [
      ["location_title1", "Titre — ligne 1"],
      ["location_title2", "Titre — ligne 2"],
      ["location_name", "Nom du restaurant"],
      ["location_address", "Adresse"],
      ["location_phone", "Téléphone"],
      ["location_map", "URL de la carte (Google Maps embed)"],
    ],
  },
  {
    title: "Réseaux sociaux & Pied de page",
    fields: [
      ["social_facebook", "Facebook (URL)"],
      ["social_instagram", "Instagram (URL)"],
      ["social_tiktok", "TikTok (URL)"],
      ["footer_text", "Texte du pied de page"],
    ],
  },
  {
    title: "Page Commander",
    fields: [
      ["order_heading", "Titre"],
      ["order_subtext", "Sous-titre"],
      ["order_info_title", "Titre des horaires"],
      ["order_info_lines", "Horaires de retrait (une ligne par service)", "textarea"],
      ["order_info_note", "Note de bas de bloc", "textarea"],
      ["order_traiteur", "Bloc Service Traiteur (vide = masqué)", "textarea"],
    ],
  },
  {
    title: "Page Réserver",
    fields: [
      ["reservation_heading", "Titre"],
      ["reservation_subtext", "Sous-titre"],
    ],
  },
  {
    title: "Navigation (barre du haut)",
    fields: [
      ["nav_home", "Bouton Accueil"],
      ["nav_order", "Bouton Commander"],
      ["nav_reserve", "Bouton Réserver"],
    ],
  },
  {
    title: "Bandeaux défilants (séparer les mots par •)",
    fields: [
      ["marquee1", "Bandeau 1 (après le hero)", "textarea"],
      ["marquee2", "Bandeau 2 (avant la localisation)", "textarea"],
    ],
  },
  {
    title: "Portfolio & Plat du jour",
    fields: [
      ["dishes_title", "Titre du carrousel"],
      ["dishes_more", "Bouton « Voir plus »"],
      ["dishofday_label", "Étiquette du Plat du jour"],
      ["dishofday_btn", "Bouton du Plat du jour"],
    ],
  },
  {
    title: "Page Galerie",
    fields: [
      ["gallery_header", "Titre dans la barre du haut"],
      ["gallery_title", "Grand titre"],
      ["gallery_back", "Bouton retour"],
    ],
  },
];

export default function AdminSitePage() {
  const [user, setUser] = useAdminAuth();
  const [site, setSite] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [uploading, setUploading] = useState(null);

  const load = useCallback(async () => {
    const [s, w] = await Promise.all([
      axios.get(`${API}/site-content`),
      axios.get(`${API}/weekly-hours`),
    ]);
    setSite(s.data);
    setWeekly({ ...DEFAULT_WEEKLY, ...(w.data.days || {}) });
  }, []);

  useEffect(() => {
    if (!user) return;
    load().catch(() => {});
  }, [user, load]);

  const set = (key) => (e) =>
    setSite((prev) => ({ ...prev, [key]: e.target.value }));

  const setDay = (day, key, val) =>
    setWeekly((prev) => ({
      ...prev,
      [day]: { ...prev[day], [key]: val },
    }));

  const save = async (e) => {
    e.preventDefault();
    try {
      await Promise.all([
        axios.put(`${API}/site-content`, site, { withCredentials: true }),
        axios.put(
          `${API}/weekly-hours`,
          { days: weekly },
          { withCredentials: true }
        ),
      ]);
      invalidateSiteCache();
      invalidateWeeklyCache();
      toast.success("Site mis à jour !");
    } catch {
      toast.error("Impossible d'enregistrer. Reconnectez-vous.");
    }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(0);
    try {
      const { data } = await axios.post(`${API}/upload-image`, fd, {
        withCredentials: true,
        onUploadProgress: (e) =>
          setUploading(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      setSite((prev) => ({ ...prev, hero_image: data.url }));
      toast.success("Photo téléchargée !");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Impossible d'envoyer l'image."
      );
    } finally {
      setUploading(null);
    }
  };

  if (user === null || (user && (site === null || weekly === null)))
    return <AdminLoading />;

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F1EB] px-5">
        <AdminLogin onSuccess={setUser} title="Modifier le Site" />
      </div>
    );
  }

  return (
    <div
      data-testid="admin-site-page"
      className="min-h-screen bg-[#F4F1EB] text-[#102C1F]"
    >
      <AdminBar />
      <header className="bg-[#102C1F] text-[#F4F1EB]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <span className="flex items-center gap-3 font-display text-xl italic md:text-2xl">
            <Globe size={20} /> Modifier le Site
          </span>
          <Link
            data-testid="site-dashboard-link"
            to="/admin"
            className="rounded-full border border-[#F4F1EB]/30 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#F4F1EB] hover:text-[#102C1F]"
          >
            Gestion
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-12 md:px-10">
        <form onSubmit={save} className="space-y-10">
          <section>
            <h2 className="mb-5 font-display text-2xl italic">
              Photo de fond du Hero
            </h2>
            {site.hero_image && (
              <img
                src={site.hero_image}
                alt="Hero"
                className="mb-4 aspect-video w-full rounded-sm object-cover"
              />
            )}
            <label
              data-testid="site-hero-upload"
              className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#102C1F]/30 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#102C1F]/70 transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
            >
              <input
                data-testid="site-hero-file"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  uploadHero(e.target.files[0]);
                  e.target.value = "";
                }}
              />
              {uploading != null
                ? `Envoi ${uploading}%`
                : "Télécharger une photo"}
            </label>
            <input
              data-testid="site-hero-image"
              value={site.hero_image}
              placeholder="ou collez une URL https://..."
              onChange={set("hero_image")}
              className={`${field} mt-2`}
            />
          </section>

          <section data-testid="weekly-hours-editor">
            <h2 className="mb-5 font-display text-2xl italic">
              Horaires par jour
            </h2>
            <div className="space-y-3">
              {DAYS.map(([key, label]) => (
                <div
                  key={key}
                  data-testid={`weekly-row-${key}`}
                  className="grid grid-cols-2 items-center gap-3 sm:grid-cols-[110px_1fr_1fr_110px]"
                >
                  <span className="text-xs uppercase tracking-[0.2em] text-[#102C1F]/70">
                    {label}
                  </span>
                  <input
                    data-testid={`weekly-${key}-lunch`}
                    value={weekly[key].lunch}
                    placeholder="Déjeuner (ex : 12h00–15h00)"
                    disabled={weekly[key].closed}
                    onChange={(e) => setDay(key, "lunch", e.target.value)}
                    className={`${field} ${weekly[key].closed ? "opacity-40" : ""}`}
                  />
                  <input
                    data-testid={`weekly-${key}-dinner`}
                    value={weekly[key].dinner}
                    placeholder="Dîner (ex : 19h00–23h00)"
                    disabled={weekly[key].closed}
                    onChange={(e) => setDay(key, "dinner", e.target.value)}
                    className={`${field} ${weekly[key].closed ? "opacity-40" : ""}`}
                  />
                  <label className="flex cursor-pointer items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#102C1F]/70">
                    <input
                      data-testid={`weekly-${key}-closed`}
                      type="checkbox"
                      checked={weekly[key].closed}
                      onChange={(e) => setDay(key, "closed", e.target.checked)}
                      className="h-4 w-4 accent-[#102C1F]"
                    />
                    Fermé
                  </label>
                </div>
              ))}
            </div>
          </section>

          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h2 className="mb-5 font-display text-2xl italic">
                {section.title}
              </h2>
              <div className="grid gap-5 sm:grid-cols-2">
                {section.fields.map(([key, label, kind]) => (
                  <label
                    key={key}
                    className={`block ${kind === "textarea" ? "sm:col-span-2" : ""}`}
                  >
                    <span className={labelCls}>{label}</span>
                    {kind === "textarea" ? (
                      <textarea
                        data-testid={`site-${key}`}
                        rows={3}
                        value={site[key] || ""}
                        onChange={set(key)}
                        className={`${field} resize-none`}
                      />
                    ) : (
                      <input
                        data-testid={`site-${key}`}
                        value={site[key] || ""}
                        onChange={set(key)}
                        className={field}
                      />
                    )}
                  </label>
                ))}
              </div>
            </section>
          ))}

          <button
            data-testid="site-save-btn"
            type="submit"
            className="rounded-full bg-[#102C1F] px-8 py-3.5 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
          >
            Enregistrer le site
          </button>
        </form>
      </main>
    </div>
  );
}
